/**
 * Browser-side helpers for signing in and up with an identity provider — Zoho and Google
 * (docs/adr/0033-zoho-sign-in.md).
 *
 * THE START PAGE AND THE CALLBACK PAGE MUST SHARE AN ORIGIN. A provider redirects back to exactly one
 * registered URL (Zoho allows no wildcards), and the nonce that proves "this browser started this
 * sign-in" lives in this origin's `sessionStorage`. So a sign-in begun anywhere else — a company's
 * own subdomain, say — is first sent to that origin's start page, and only then to the provider.
 *
 * THAT ORIGIN IS `app.<APP_DOMAIN>`, NOT THE BARE DOMAIN. `app` is a reserved subdomain (no company can
 * claim it) that the wildcard vhost already serves with this web app, so it works today and keeps
 * working after the bare domain moves over. The bare domain is NOT ours while it is still the legacy
 * site — sending people there is a nginx 404 (what the first version of this did). The API derives its
 * default redirect URI by the same rule (apps/api/src/lib/sso/providers.ts), so the two cannot drift.
 */

import { Capacitor } from '@capacitor/core';
import {
  appChallengeSchema,
  ssoProviderSchema,
  type SsoProvider,
  type SsoPurpose,
} from '@hrms/shared';
import googleLogo from '../assets/sso/google.svg';
import zohoLogo from '../assets/sso/zoho.svg';
import { APP_DOMAIN, currentOrgSlug, isDevOffAppDomain, orgSubdomainOrigin } from './subdomain.js';

/**
 * The origin the start and callback pages run on. Local development has no such subdomain to hop to
 * (`localhost` is the registered redirect), so it stays where it is.
 */
export function ssoOrigin(): string {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || isDevOffAppDomain()) {
    return window.location.origin;
  }
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${window.location.protocol}//app.${APP_DOMAIN}${port}`;
}

const NONCE_KEY = 'sso_signin_nonce';

/** What the button says. */
export const SSO_PROVIDER_LABELS: Record<SsoProvider, string> = { zoho: 'Zoho', google: 'Google' };

/** The provider's logo, an SVG file so its brand colours live in the asset and not in a component. */
export const SSO_PROVIDER_LOGOS: Record<SsoProvider, string> = {
  zoho: zohoLogo,
  google: googleLogo,
};

/**
 * Whether the logo spells the provider's name (Zoho's wordmark) or is only a mark (Google's "G"). A
 * wordmark reads as "Continue with ZOHO"; a mark goes before the words "Continue with Google".
 */
export const SSO_PROVIDER_LOGO_IS_WORDMARK: Record<SsoProvider, boolean> = {
  zoho: true,
  google: false,
};

/** The provider named in a URL, or null — it comes off a path segment, so it is checked, not trusted. */
export function parseSsoProvider(value: string | undefined): SsoProvider | null {
  const parsed = ssoProviderSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * Offered everywhere. In the packaged native app the provider flow runs in the system browser (Google
 * refuses embedded WebViews) and comes back over `peopleorbit://sso`, PKCE-bound to the app —
 * lib/nativeSso.ts, docs/adr/0038-native-sign-in.md. Kept as a function so a caller that must stay
 * off a platform has one place to ask.
 */
export function ssoSignInAvailableHere(): boolean {
  return true;
}

/**
 * Where a provider button sends the browser. `appChallenge` (the packaged app's PKCE challenge)
 * travels as `ac`: the start page keeps it for the whole attempt and the finished sign-in returns to
 * the app instead of hopping to the company's subdomain.
 */
export function ssoStartHref(
  provider: SsoProvider,
  purpose: SsoPurpose,
  options: { orgSlug?: string | null; next?: string; appChallenge?: string } = {},
): string {
  const params = new URLSearchParams({ purpose });
  if (options.orgSlug) params.set('org', options.orgSlug);
  if (options.next && options.next !== '/') params.set('next', options.next);
  if (options.appChallenge) params.set('ac', options.appChallenge);
  const path = `/auth/${provider}/start?${params.toString()}`;
  const origin = ssoOrigin();
  return origin === window.location.origin ? path : `${origin}${path}`;
}

/**
 * Where a finished sign-in lands: the existing `/session-handoff` page on the company's own
 * subdomain, which redeems the one-time code for a session of its own (tokens are per-origin).
 * Stays on this origin wherever a subdomain hop cannot work — same exceptions as Login.tsx,
 * including the phone app: its pages are packaged inside it, and hopping to `acme.<domain>` would
 * load the live website in the app's window instead (a phone-code sign-up ends here).
 */
export function ssoHandoffHref(orgSlug: string, code: string, next: string): string {
  const host = window.location.hostname;
  const stayHere =
    currentOrgSlug() === orgSlug ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    isDevOffAppDomain() ||
    Capacitor.isNativePlatform();
  const origin = stayHere ? window.location.origin : orgSubdomainOrigin(orgSlug);
  return `${origin}/session-handoff?code=${encodeURIComponent(code)}&next=${encodeURIComponent(next)}`;
}

/** Same-origin paths only — it comes off a URL, which is where open redirects come from. */
export function safeNextPath(value: string | null | undefined): string | undefined {
  return value && value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')
    ? value.slice(0, 512)
    : undefined;
}

// Storage can throw (private windows, blocked site data); a missing nonce then simply fails the
// sign-in closed, which is the right outcome.
export function saveSsoNonce(nonce: string): boolean {
  try {
    window.sessionStorage.setItem(NONCE_KEY, nonce);
    return true;
  } catch {
    return false;
  }
}

/** Reads and removes: a nonce is good for one callback. */
export function takeSsoNonce(): string | null {
  try {
    const nonce = window.sessionStorage.getItem(NONCE_KEY);
    window.sessionStorage.removeItem(NONCE_KEY);
    return nonce;
  } catch {
    return null;
  }
}

/**
 * The packaged app's PKCE challenge, kept for the BROWSER TAB that runs its sign-in. It is set when
 * the start page sees `ac`, and read — not consumed — by the callback, the workspace chooser and the
 * sign-up page, which each decide whether to finish with `ReturnToApp`. It is cleared only when the
 * tab hands the code back to the app (or a fresh start comes without one). sessionStorage, like the
 * nonce: it must not outlive the tab, and a plain web sign-in never has one.
 */
const APP_CHALLENGE_KEY = 'sso_app_challenge';

/** Stores the challenge if it is well formed; anything else is dropped so it can never be echoed. */
export function saveSsoAppChallenge(challenge: string): boolean {
  if (!appChallengeSchema.safeParse(challenge).success) return false;
  try {
    window.sessionStorage.setItem(APP_CHALLENGE_KEY, challenge);
    return true;
  } catch {
    return false;
  }
}

/** The stored challenge, or null. Does NOT remove it (see above). */
export function peekSsoAppChallenge(): string | null {
  try {
    const value = window.sessionStorage.getItem(APP_CHALLENGE_KEY);
    return value && appChallengeSchema.safeParse(value).success ? value : null;
  } catch {
    return null;
  }
}

export function clearSsoAppChallenge(): void {
  try {
    window.sessionStorage.removeItem(APP_CHALLENGE_KEY);
  } catch {
    // Nothing to clear if storage is blocked.
  }
}

/**
 * What the callback hands the sign-up page after a provider verified the person: prefill for the
 * form, and the signed proof `register` will trust. Carried in router state, not the URL — the proof
 * is a bearer value that has no business in a history entry or a referrer.
 */
export interface SsoSignupState {
  provider: SsoProvider;
  proof: string;
  name: string;
  email: string;
}

export function readSsoSignupState(state: unknown): SsoSignupState | null {
  const sso = (state as { sso?: Record<string, unknown> } | null)?.sso;
  if (!sso || typeof sso !== 'object') return null;
  const provider = parseSsoProvider(typeof sso.provider === 'string' ? sso.provider : undefined);
  if (
    !provider ||
    typeof sso.proof !== 'string' ||
    typeof sso.name !== 'string' ||
    typeof sso.email !== 'string'
  ) {
    return null;
  }
  return { provider, proof: sso.proof, name: sso.name, email: sso.email };
}
